import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { FormikProps } from 'formik';
import React from 'react';

import { Box, Button, Flex, Icon, Text } from '~/components/blocks';
import {
  EMPTY_TIME_RANGE,
  ORGANIZATION_BUSINESS_HOUR_MAX_COUNT,
  TimeRange,
} from '~/constants/organization_business_hour';

import { Fields } from '../types';
import { TimeSelect } from './TimeSelect';

const ButtonDelete = styled('button')(({ theme }) =>
  css({
    display: 'flex',
    width: '30px',
    height: '30px',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.colors.background.bg,
    border: theme.borders.transparent,
    borderRadius: theme.radii.circle,
    marginLeft: theme.space.l,
    outline: 'none',
    transitionDuration: theme.transitions.fast,
    '&:hover': {
      background: theme.colors.background.default,
      border: theme.borders.default,
      cursor: 'pointer',
    },
    '&:disabled': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  }),
);

const Root = styled('div')(({ theme }) =>
  css({
    '& + &': {
      marginTop: theme.space.m,
    },
  }),
);

type Props = {
  formik: FormikProps<Fields>;
  disabled: boolean;
  dayIdx: number;
  timeRanges: TimeRange[];
};

export const BusinessHoursField = (props: Props) => {
  const { formik, disabled, dayIdx, timeRanges } = props;
  const theme = useTheme();
  const fieldPath = `businessHours[${dayIdx}].timeRanges`;

  const handleAddRange = () => {
    formik.setFieldValue(fieldPath, [...timeRanges, { ...EMPTY_TIME_RANGE }]);
  };

  const handleRemoveRange = (rangeIdx: number) => {
    formik.setFieldValue(
      fieldPath,
      timeRanges.filter((_, i) => i !== rangeIdx),
    );
  };

  const createStartTimeHandlers = (rangeIdx: number, currentMinute: string) => {
    const rangePath = `${fieldPath}[${rangeIdx}]`;

    const handleHourChange = (newHour: string) => {
      formik.setFieldValue(`${rangePath}.startHour`, newHour);
      if (newHour === '24' && currentMinute !== '--' && currentMinute !== '00') {
        formik.setFieldValue(`${rangePath}.startMinute`, '00');
      }
    };

    const handleMinuteChange = (newMinute: string) => {
      formik.setFieldValue(`${rangePath}.startMinute`, newMinute);
    };

    return {
      handleHourChange,
      handleMinuteChange,
    };
  };

  const createEndTimeHandlers = (rangeIdx: number, currentMinute: string) => {
    const rangePath = `${fieldPath}[${rangeIdx}]`;

    const handleHourChange = (newHour: string) => {
      formik.setFieldValue(`${rangePath}.endHour`, newHour);
      if (newHour === '24' && currentMinute !== '--' && currentMinute !== '00') {
        formik.setFieldValue(`${rangePath}.endMinute`, '00');
      }
    };

    const handleMinuteChange = (newMinute: string) => {
      formik.setFieldValue(`${rangePath}.endMinute`, newMinute);
    };

    return {
      handleHourChange,
      handleMinuteChange,
    };
  };

  return (
    <>
      {timeRanges.map((range, rangeIdx) => {
        const startHandlers = createStartTimeHandlers(rangeIdx, range.startMinute);
        const endHandlers = createEndTimeHandlers(rangeIdx, range.endMinute);

        return (
          <Root key={rangeIdx}>
            <Flex alignItems="center">
              <TimeSelect
                disabled={disabled}
                hour={range.startHour}
                minute={range.startMinute}
                onHourChange={startHandlers.handleHourChange}
                onMinuteChange={startHandlers.handleMinuteChange}
              />
              <Text mx={theme.space.xs} size="s">
                〜
              </Text>
              <TimeSelect
                disabled={disabled}
                hour={range.endHour}
                minute={range.endMinute}
                onHourChange={endHandlers.handleHourChange}
                onMinuteChange={endHandlers.handleMinuteChange}
              />
              {timeRanges.length > 1 && (
                <ButtonDelete disabled={disabled} onClick={() => handleRemoveRange(rangeIdx)}>
                  <Icon icon="close2" size="l" />
                </ButtonDelete>
              )}
            </Flex>
          </Root>
        );
      })}
      {timeRanges.length < ORGANIZATION_BUSINESS_HOUR_MAX_COUNT && (
        <Box textAlign="right">
          <Button use="white" disabled={disabled} onClick={handleAddRange}>
            <Icon icon="plus" size="l" />
            追加
          </Button>
        </Box>
      )}
    </>
  );
};
